/**
 * useQRCodes - Hook for QR code CRUD operations
 */

import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getAdminQRCodes,
  createAdminQRCode,
  updateAdminQRCode,
  deleteAdminQRCode,
  saveQRImage as saveQRImageRequest,
  resetQRStats as resetQRStatsRequest,
} from '@/lib/adminQrCodesApi';
import type { QRCode, CreateQRCodeRequest, UpdateQRCodeRequest } from '@/types/qrCode';

interface UseQRCodesReturn {
  qrCodes: QRCode[];
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  fetchQRCodes: (filters?: { unit_id?: string; is_active?: boolean }) => Promise<void>;
  createQRCode: (data: CreateQRCodeRequest) => Promise<QRCode | null>;
  updateQRCode: (id: string, data: UpdateQRCodeRequest) => Promise<QRCode | null>;
  deleteQRCode: (id: string) => Promise<boolean>;
  saveQRImage: (id: string, blob: Blob) => Promise<QRCode | null>;
  resetStats: (id: string) => Promise<boolean>;
}

export function useQRCodes(): UseQRCodesReturn {
  const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQRCodes = useCallback(async (filters?: { unit_id?: string; is_active?: boolean }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAdminQRCodes(filters);
      setQRCodes(result.data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch QR codes';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createQRCode = useCallback(async (data: CreateQRCodeRequest): Promise<QRCode | null> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await createAdminQRCode(data);
      toast.success('QR code created');
      return result.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create QR code';
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const updateQRCode = useCallback(async (id: string, data: UpdateQRCodeRequest): Promise<QRCode | null> => {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await updateAdminQRCode(id, data);
      return result.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update QR code';
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const deleteQRCode = useCallback(async (id: string): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      await deleteAdminQRCode(id);
      toast.success('QR code deleted');
      return true;
    } catch {
      toast.error('Failed to delete QR code');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const saveQRImage = useCallback(async (id: string, blob: Blob): Promise<QRCode | null> => {
    setIsSubmitting(true);
    try {
      const result = await saveQRImageRequest(id, blob);
      toast.success('QR image saved');
      return result.data;
    } catch {
      toast.error('Failed to save QR image');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const resetStats = useCallback(async (id: string): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      await resetQRStatsRequest(id);
      toast.success('Scan count reset');
      return true;
    } catch {
      toast.error('Failed to reset stats');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    qrCodes,
    isLoading,
    isSubmitting,
    error,
    fetchQRCodes,
    createQRCode,
    updateQRCode,
    deleteQRCode,
    saveQRImage,
    resetStats,
  };
}
